/**
 * Grouping and user guidance for protocol validation findings.
 */

import type { ProtocolValidationFinding } from "../../api/clinicalTrialsApiTypes";

export type ProtocolFindingGroup = {
  key: string;
  code: string;
  title: string;
  guidance: string;
  findings: ProtocolValidationFinding[];
};

const FINDING_GUIDANCE: Record<string, { title: string; guidance: string }> = {
  "criterion.rationale_missing": {
    title: "Criterion rationale is missing",
    guidance:
      "Explain why each criterion has its assigned priority or tier. The CSV import did not provide this field.",
  },
  "atom.needs_review": {
    title: "Atom requires clinical review",
    guidance:
      "Review the atom wording and metadata, then clear its review flag when clinically approved.",
  },
  "dag.cycle": {
    title: "Execution cycle detected",
    guidance:
      "Remove or redirect one of the execution dependencies so the protocol remains acyclic.",
  },
  "connection.missing_endpoint": {
    title: "Connection endpoint is missing",
    guidance:
      "Remove the broken connection or restore its referenced atom, gate, or criterion.",
  },
};

/**
 * Group repeated findings and attach concise remediation guidance.
 *
 * @param findings - Raw validation findings
 * @returns Findings grouped by stable code and message
 */
export function groupValidationFindings(
  findings: ProtocolValidationFinding[],
): ProtocolFindingGroup[] {
  const groups = new Map<string, ProtocolValidationFinding[]>();
  for (const finding of findings) {
    const key = `${finding.code}:${finding.message}`;
    groups.set(key, [...(groups.get(key) ?? []), finding]);
  }

  return [...groups.entries()].map(([key, groupedFindings]) => {
    const first = groupedFindings[0];
    const known = FINDING_GUIDANCE[first.code];
    return {
      key,
      code: first.code,
      title: known?.title ?? first.message,
      guidance:
        known?.guidance ??
        "Inspect the affected entities and update the draft before validating again.",
      findings: groupedFindings,
    };
  });
}
